import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight, TrendingUp } from "lucide-react";
import { fetchArticles, fetchArtists, fetchAdvertorials } from "@/lib/api";
import { ArticleCard } from "@/components/ArticleCard";
import { RankBadge } from "@/components/RankBadge";
import { AdSlot } from "@/components/AdSlot";
import { Seo } from "@/components/Seo";

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [latest, setLatest] = useState([]);
  const [artists, setArtists] = useState([]);
  const [sponsored, setSponsored] = useState([]);

  useEffect(() => {
    fetchArticles({ featured: true }).then(setFeatured).catch(() => {});
    fetchArticles({ limit: 7 }).then(setLatest).catch(() => {});
    fetchArtists().then((d) => setArtists(d.slice(0, 5))).catch(() => {});
    fetchAdvertorials().then((d) => setSponsored(d.slice(0, 3))).catch(() => {});
  }, []);

  const hero = latest[0];
  const secondary = featured.filter((a) => a.slug !== hero?.slug).slice(0, 2);
  const drops = latest.slice(1, 7);

  return (
    <div>
      <Seo path="/" image={hero?.image} />
      {/* HERO */}
      {hero && (
        <Link
          to={`/article/${hero.slug}`}
          data-testid="hero-article"
          className="group relative block grain overflow-hidden border-b border-white/10"
        >
          <div className="relative h-[78vh] min-h-[520px]">
            <img src={hero.image} alt={hero.title} className="img-treatment w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-black/10" />
            <div className="absolute inset-0 max-w-[1400px] mx-auto px-5 lg:px-10 flex flex-col justify-end pb-16">
              <span className="font-mono text-xs uppercase tracking-[0.3em] text-signal mb-4 animate-rise">
                Cover Story / {hero.category}
              </span>
              <h1 className="font-display font-semibold tracking-tighter leading-[0.92] text-5xl md:text-7xl lg:text-[5.5rem] max-w-5xl animate-rise">
                {hero.title}
              </h1>
              <p className="text-foreground/85 text-base md:text-lg mt-6 max-w-2xl leading-relaxed animate-rise">
                {hero.dek}
              </p>
              <span className="inline-flex items-center gap-2 mt-7 font-mono text-xs uppercase tracking-widest text-foreground group-hover:text-signal transition-colors">
                Read the feature <ArrowUpRight className="w-4 h-4" />
              </span>
            </div>
          </div>
        </Link>
      )}

      <div className="max-w-[1400px] mx-auto px-5 lg:px-10">
        {/* SECONDARY FEATURES */}
        <section className="py-16">
          <SectionHead title="The Front Page" link="/c/music" label="More stories" />
          <div className="grid md:grid-cols-2 gap-6 mt-8">
            {secondary.map((a, i) => (
              <ArticleCard key={a.slug} article={a} size="lg" index={i} />
            ))}
          </div>
        </section>

        {/* AD — leaderboard */}
        <AdSlot variant="leaderboard" slot={process.env.REACT_APP_ADSENSE_SLOT_HOME} className="mb-4" />

        {/* RANKINGS PREVIEW */}
        <section className="py-16 border-t border-white/10">
          <SectionHead
            title="The Power Index"
            link="/rankings"
            label="Full rankings"
            icon={<TrendingUp className="w-5 h-5 text-signal" />}
          />
          <p className="text-muted-foreground text-sm mt-3 max-w-2xl">
            We rank the artists running global culture — scored automatically from Apple Music charts
            across the US, UK, Europe, K-Pop, Canada and Afrobeats, plus press buzz and fanbase. Here's who's running it right now.
          </p>
          <div className="mt-8 border border-white/10 divide-y divide-white/10">
            {artists.map((a) => (
              <Link
                key={a.slug}
                to={`/artist/${a.slug}`}
                data-testid={`rank-preview-${a.slug}`}
                className="group flex items-center gap-4 md:gap-6 p-4 md:px-6 hover:bg-secondary transition-colors"
              >
                <span className="font-mono-stat text-2xl md:text-3xl text-signal w-12 tabular-nums">
                  {String(a.rank).padStart(2, "0")}
                </span>
                <img src={a.image} alt={a.name} className="w-12 h-12 md:w-14 md:h-14 object-cover grayscale" />
                <div className="flex-1 min-w-0">
                  <div className="font-display text-lg md:text-xl font-medium group-hover:text-signal transition-colors truncate">
                    {a.name}
                  </div>
                  <div className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground truncate">
                    {a.title} · {a.city}
                  </div>
                </div>
                <RankBadge movement={a.movement} />
                <span className="font-mono-stat text-xl md:text-2xl tabular-nums w-16 text-right">
                  {a.overall}
                </span>
              </Link>
            ))}
          </div>
        </section>

        {/* SPONSORED */}
        {sponsored.length > 0 && (
          <section className="py-16 border-t border-white/10">
            <SectionHead title="Paid Partnerships" link="/advertorials" label="All sponsored" />
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
              {sponsored.map((a, i) => (
                <Link
                  key={a.slug}
                  to={`/advertorial/${a.slug}`}
                  data-testid={`home-sponsored-${a.slug}`}
                  className="group block border border-white/10 bg-card hover:border-signal transition-colors animate-rise"
                  style={{ animationDelay: `${i * 60}ms` }}
                >
                  <div className="relative overflow-hidden aspect-[4/3]">
                    <img src={a.image} alt={a.title} className="img-treatment w-full h-full object-cover" loading="lazy" />
                    <span className="absolute top-3 left-3 font-mono text-[10px] uppercase tracking-widest bg-white text-black px-2 py-1">
                      Sponsored
                    </span>
                  </div>
                  <div className="p-5">
                    <span className="font-mono text-[10px] uppercase tracking-widest text-signal">{a.brand}</span>
                    <h3 className="font-display text-lg font-semibold tracking-tight leading-[1.05] mt-2 group-hover:text-signal transition-colors">
                      {a.title}
                    </h3>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* LATEST */}
        <section className="py-16 border-t border-white/10">
          <SectionHead title="Latest Drops" link="/c/fashion" label="Browse all" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
            {drops.map((a, i) => (
              <ArticleCard key={a.slug} article={a} index={i} />
            ))}
          </div>
        </section>

        {/* AD — leaderboard */}
        <AdSlot variant="leaderboard" slot={process.env.REACT_APP_ADSENSE_SLOT_HOME2} className="mb-16" />
      </div>
    </div>
  );
}

function SectionHead({ title, link, label, icon }) {
  return (
    <div className="flex items-end justify-between gap-4">
      <h2 className="font-anton text-3xl md:text-4xl tracking-tight uppercase flex items-center gap-3">
        {icon}
        {title}
      </h2>
      {link && (
        <Link
          to={link}
          className="font-mono text-xs uppercase tracking-widest text-muted-foreground hover:text-signal transition-colors flex items-center gap-1 whitespace-nowrap"
        >
          {label} <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      )}
    </div>
  );
}
